import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X } from "lucide-react";

const MAX_IMAGES = 8;

export default function ProductEditor({ mode = "new" }) {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: 0,
    currency: "TRY",
    images: [""],
    shopier_url: "",
    product_type: "fiziksel",
    category: "",
    in_stock: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(mode === "new");

  useEffect(() => {
    api.get("/product-categories").then(({ data }) => {
      setCategories(data);
      if (!form.category && data.length && mode === "new") {
        setForm((f) => ({ ...f, category: data[0].slug }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode === "edit" && productId) {
      api.get(`/products/${productId}`).then(({ data }) => {
        const images = data.images?.length ? data.images : data.image_url ? [data.image_url] : [""];
        setForm({
          title: data.title,
          description: data.description,
          price: data.price,
          currency: data.currency || "TRY",
          images,
          shopier_url: data.shopier_url || "",
          product_type: data.product_type,
          category: data.category,
          in_stock: data.in_stock,
        });
        setLoaded(true);
      });
    }
  }, [mode, productId]);

  const setImageAt = (i, value) => {
    setForm((f) => ({ ...f, images: f.images.map((img, idx) => (idx === i ? value : img)) }));
  };
  const addImageField = () => {
    setForm((f) => (f.images.length >= MAX_IMAGES ? f : { ...f, images: [...f.images, ""] }));
  };
  const removeImageField = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form, price: Number(form.price), images: form.images.map((u) => u.trim()).filter(Boolean) };
      let result;
      if (mode === "new") {
        ({ data: result } = await api.post("/products", payload));
      } else {
        ({ data: result } = await api.patch(`/products/${productId}`, payload));
      }
      toast.success(mode === "new" ? "Ürün eklendi" : "Ürün güncellendi");
      navigate(`/magaza/${result.slug || result.product_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="k-container py-16"><div className="k-card h-96 animate-pulse" /></div>;

  return (
    <div className="k-container py-12 md:py-16">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-5xl text-ink-900" data-testid="product-editor-title">
          {mode === "new" ? "Yeni ürün" : "Ürünü düzenle"}
        </h1>

        <form onSubmit={submit} className="k-card mt-8 space-y-5 p-8" data-testid="product-editor-form">
          <div>
            <label className="k-label">Başlık</label>
            <input
              className="k-input"
              required
              minLength={2}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="product-title-input"
            />
          </div>
          <div>
            <label className="k-label">Açıklama</label>
            <textarea
              rows={4}
              className="k-input resize-none"
              required
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="product-description-input"
            />
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="k-label">Fiyat</label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="k-input"
                required
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="product-price-input"
              />
            </div>
            <div>
              <label className="k-label">Para birimi</label>
              <input
                className="k-input"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                data-testid="product-currency-input"
              />
            </div>
          </div>
          <div>
            <label className="k-label">Ürün fotoğrafları</label>
            <div className="space-y-2">
              {form.images.map((img, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className="k-input"
                    value={img}
                    onChange={(e) => setImageAt(i, e.target.value)}
                    placeholder="https://…"
                    data-testid={`product-image-input-${i}`}
                  />
                  {img && (
                    <img
                      src={img}
                      alt={`Önizleme ${i + 1}`}
                      className="h-11 w-11 shrink-0 rounded-lg object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}
                  {form.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImageField(i)}
                      className="k-btn-ghost !p-2 shrink-0 text-destructive"
                      data-testid={`remove-product-image-${i}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {form.images.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={addImageField}
                className="k-btn-ghost mt-2 text-sm"
                data-testid="add-product-image-btn"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Fotoğraf ekle
              </button>
            )}
            <p className="mt-1 font-body text-xs text-ink-500">İlk fotoğraf, kart görünümünde kapak olarak kullanılır. En fazla {MAX_IMAGES} fotoğraf.</p>
          </div>
          <div>
            <label className="k-label">Shopier Linki</label>
            <input
              className="k-input"
              value={form.shopier_url}
              onChange={(e) => setForm({ ...form, shopier_url: e.target.value })}
              placeholder="https://www.shopier.com/…"
              data-testid="product-shopier-url-input"
            />
            <p className="mt-1 font-body text-xs text-ink-500">
              "Satın Al" butonu bu linke yeni sekmede yönlendirir. Boş bırakılırsa buton "Yakında" olarak görünür.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="k-label">Kategori</label>
              <select
                className="k-input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="product-category-select"
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="k-label">Tür</label>
              <div className="flex gap-2">
                {["fiziksel", "dijital"].map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setForm({ ...form, product_type: t })}
                    className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold ${
                      form.product_type === t
                        ? "border-sage-500 bg-sage-500 text-cream-50"
                        : "border-cream-300 bg-cream-50 text-ink-700"
                    }`}
                    data-testid={`product-type-${t}`}
                  >
                    {t === "dijital" ? "Dijital" : "Fiziksel"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {form.product_type === "fiziksel" && (
            <label className="flex items-center gap-3 rounded-xl bg-cream-200/50 p-3 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={form.in_stock}
                onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                className="h-4 w-4 accent-sage-500"
                data-testid="product-instock-checkbox"
              />
              <span>Stokta var</span>
            </label>
          )}

          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={busy} className="k-btn-primary disabled:opacity-60" data-testid="product-submit-btn">
              {busy ? "Kaydediliyor…" : mode === "new" ? "Ürünü yayınla" : "Kaydet"}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="k-btn-ghost">
              Vazgeç
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
